<template>
  <div class="expenses-mobile-view p-2">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="mb-0 font-weight-bold">
        {{ $t(titleKey) }}
      </h4>
      <income-total-card
        :label="'totalExcomes'"
        :total-income="total"
        small
      />
    </div>

    <!-- Botón de filtros -->
    <div class="d-flex justify-content-end mb-2">
      <b-button
        variant="outline-primary"
        class="mb-2"
        @click="showSidebar = true"
      >
        <feather-icon
          icon="FilterIcon"
          class="mr-1"
        />
        {{ $t('filters') }}
        <b-badge
          v-if="activeFiltersCount > 0"
          variant="danger"
          class="ml-2"
        >
          {{ activeFiltersCount }}
        </b-badge>
      </b-button>
    </div>

    <!-- Tabla real con gastos -->
    <b-overlay
      :show="loading"
      class="overlay-content"
    >
      <expenses-table
        :expenses="expenses"
        :is-material="false"
        :project-id="scopeProjectId"
        :customer-id="scopeCustomerId"
        :show-project-column="showProjectColumn"
        :grouped-expense-project-map="groupedExpenseProjectMap"
        @sort-changed="handleSortChange"
      />
      <div
        v-if="!expenses.length && !loading"
        class="text-center text-muted mt-3"
      >
        {{ $t('noData') }}
      </div>
    </b-overlay>

    <!-- Paginación -->
    <div class="d-flex justify-content-center my-3">
      <b-pagination
        size="sm"
        :value="expensesPagination.page"
        :total-rows="expensesPagination.total_objects"
        :per-page="expensesPagination.per_page"
        @change="nextPage"
      />
    </div>

    <!-- Botón flotante -->
    <div
      v-if="scopeProjectId"
      class="float-button"
    >
      <b-button
        :to="{
          name: 'newExpense',
          params: { projectId: scopeProjectId },
          query: { createType: 'direct_cost' },
        }"
        variant="primary"
        class="add-button"
      >
        <PlusIcon size="20" />
      </b-button>
    </div>

    <!-- Sidebar de filtros -->
    <b-sidebar
      v-model="showSidebar"
      right
      :title="$t('filters')"
      shadow
      backdrop
      bg-variant="white"
      style="width: 300px; max-width: 100%;"
    >
      <div class="p-3">

        <b-form @submit.prevent="applyFilters">
          <b-form-group
            :label="$t('fromDate')"
            label-for="from-date"
            class="mb-3"
          >
            <b-form-datepicker
              id="from-date"
              v-model="query.dates.begin"
              reset-button
              class="w-100"
            />
          </b-form-group>

          <b-form-group
            :label="$t('toDate')"
            label-for="to-date"
            class="mb-3"
          >
            <b-form-datepicker
              id="to-date"
              v-model="query.dates.end"
              reset-button
              class="w-100"
            />
          </b-form-group>

          <b-form-group
            :label="$t('status')"
            label-for="status"
            class="mb-3"
          >
            <b-form-select
              id="status"
              v-model="query.status"
              :options="expenseStatusOptions"
              class="w-100"
            />
          </b-form-group>

          <b-form-group
            :label="$t('name')"
            label-for="name"
            class="mb-3"
          >
            <b-form-input
              id="name"
              v-model="query.name"
              autocomplete="off"
              type="text"
              class="w-100"
            />
          </b-form-group>

          <b-form-group
            :label="$t('supplier')"
            label-for="supplier"
            class="mb-3"
          >
            <b-form-select
              id="supplier"
              v-model="query.supplier"
              :options="supplierOptions"
              class="w-100"
            />
          </b-form-group>

          <b-form-group
            :label="$t('category')"
            label-for="category"
            class="mb-3"
            v-if="showCategoryFilters"
          >
            <b-form-select
              id="category"
              v-model="query.expensetypecatalog"
              :options="expensetypecatalogOptions"
              class="w-100"
              @change="onCategoryChange(query.expensetypecatalog)"
            />
          </b-form-group>

          <template v-if="showCategoryFilters">
            <b-form-group
              v-for="(subcategoryOptions, index) in subcategories"
              :key="index"
              :label="$t('subcategory') + ' ' + (index + 1)"
              class="mb-3"
            >
              <b-form-select
                v-model="selectedSubcategories[index]"
                :options="[
                  { text: $t('noSubcategory'), value: null },
                  ...subcategoryOptions.map(subcat => ({
                    text: subcat.name,
                    value: subcat.id,
                  }))
                ]"
                class="w-100"
                @change="onSubcategoryChange(selectedSubcategories[index], index)"
              />
            </b-form-group>
          </template>

          <b-button
            type="submit"
            variant="primary"
            block
            class="mb-2"
          >
            {{ $t('apply') }}
          </b-button>

          <b-button
            type="button"
            variant="outline-danger"
            block
            @click="clearFilters"
          >
            {{ $t('clearFilters') }}
          </b-button>

        </b-form>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import { PlusIcon } from 'vue-feather-icons'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import IncomeTotalCard from '@/components/income/IncomeTotalCard.vue'
import ExpensesTable from '@/components/expenses/ExpensesTable.vue'

const DEFAULT_SUPPLIER = 'YoConstruyo'

export default {
  components: {
    PlusIcon,
    IncomeTotalCard,
    ExpensesTable,
  },
  props: {
    projectId: {
      type: [String, Number],
      default: null,
    },
    customerId: {
      type: [String, Number],
      default: null,
    },
    titleKey: {
      type: String,
      default: 'expenses',
    },
    showProjectColumn: {
      type: Boolean,
      default: false,
    },
    groupedExpenseProjectMap: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      loading: false,
      showSidebar: false,
      subcategories: [],
      selectedSubcategories: [],
      sortBy: 'expense_date',
      sortDesc: true,
      query: {
        dates: {},
        status: null,
        supplier: null,
        name: null,
        expensetypecatalog: null,
      },
      expensetypecatalogOptions: [
        { text: this.$t('all'), value: null },
      ],
      supplierOptions: [
        { text: this.$t('all'), value: null },
      ],

    }
  },
  computed: {
    ...mapGetters('expenses', ['expenses', 'expensesPagination', 'expensesTotal']),
    scopeProjectId() {
      return this.projectId || this.$route.params.projectId || null
    },
    scopeCustomerId() {
      return this.customerId || this.$route.params.customerId || null
    },
    showCategoryFilters() {
      return !!this.scopeProjectId
    },
    total() {
      return this.formatMoney(this.expensesTotal || 0)
    },
    activeFiltersCount() {
      let count = 0
      if (this.query.dates.begin) count += 1
      if (this.query.dates.end) count += 1
      if (this.query.status && this.query.status !== 'Todos') count += 1
      if (this.query.supplier) count += 1
      if (this.query.name?.trim()) count += 1
      if (this.showCategoryFilters) {
        if (this.query.expensetypecatalog && this.query.expensetypecatalog !== 'Todos') count += 1
        count += this.selectedSubcategories.filter(s => s !== null && s !== undefined).length
      }
      return count
    },
    expenseStatusOptions() {
      return [
        { text: this.$t('all'), value: null },
        { text: this.$t('paid'), value: 'paid' },
        { text: this.$t('credit'), value: 'credit' },
        { text: this.$t('expired'), value: 'expired' },
        { text: this.$t('topay'), value: 'topay' },
      ]
    },
  },
  mounted() {
    this.filterExpenses()
    this.loadSuppliers()
    if (this.showCategoryFilters) {
      this.fetchExpensesTypeCatalogs(this.scopeProjectId)
        .then(categories => {
          if (categories && categories.data) {
            this.expensetypecatalogOptions = [
              { text: this.$t('all'), value: null },
              ...categories.data.map(category => ({
                text: category.name,
                value: category.id,
                subcategories: category.subcategories,
              })),
            ]
          }
        })
        .catch(error => {
          console.error('❌ Error al obtener categorías:', error)
        })
    }
  },
  methods: {
    ...mapActions('expenses', ['fetchExpenses', 'fetchSuppliers']),
    ...mapActions('groupedExpenses', ['fetchSubExpenseTypeCatalog', 'fetchExpensesTypeCatalogs']),
    ...mapMutations('expenses', { changePagination: 'CHANGE_PAGINATION' }),

    clearFilters() {
      this.query = {
        dates: { begin: null, end: null },
        status: null,
        supplier: null,
        name: null,
        expensetypecatalog: null,
      }
      this.selectedSubcategories = []
      this.subcategories = []
      this.filterExpenses({ resetPage: true })
      this.showSidebar = false
    },

    nextPage(page) {
      this.changePagination({ field: 'page', value: page })
      this.filterExpenses({ resetPage: false })
    },

    applyFilters() {
      this.showSidebar = false
      this.filterExpenses({ resetPage: true })
    },

    loadSuppliers() {
      const projectId = this.scopeProjectId
      const customerId = this.scopeCustomerId
      return this.fetchSuppliers({ projectId, customerId, column: 'supplier' })
        .then(response => {
          const list = Array.isArray(response) ? response : response?.data || []
          const normalized = list
            .map(item => {
              if (typeof item === 'string') return item
              return item?.supplier || item?.name || item?.text || item?.value
            })
            .filter(Boolean)
          const unique = [...new Set(normalized)]
          if (!unique.includes(DEFAULT_SUPPLIER)) {
            unique.unshift(DEFAULT_SUPPLIER)
          }
          this.supplierOptions = [
            { text: this.$t('all'), value: null },
            ...unique.map(name => ({ text: name, value: name })),
          ]
        })
        .catch(error => {
          console.error('Error al obtener proveedores:', error)
        })
    },

    getSortColumn() {
      if (!this.sortBy) return null
      const allowed = [
        'expense_date',
        'name',
        'total',
        'expense_status',
        'supplier',
        'quantity',
        'unit_price',
        'unit_type',
        'quote_folio',
      ]
      return allowed.includes(this.sortBy) ? this.sortBy : null
    },

    filterExpenses(options = {}) {
      let resetPage = true
      if (
        typeof options === 'object'
        && options !== null
        && Object.prototype.hasOwnProperty.call(options, 'resetPage')
      ) {
        resetPage = options.resetPage
      }
      if (resetPage) {
        this.changePagination({ field: 'page', value: 1 })
      }
      let dates = {}
      if (this.query.dates.begin?.length) {
        dates.start_date = this.query.dates.begin
      }
      if (this.query.dates.end?.length) {
        dates.end_date = this.query.dates.end
      }
      if (!Object.keys(dates).length) {
        dates = null
      }

      this.loading = true

      let finalCategoryId = null
      if (this.showCategoryFilters) {
        // Usa la última subcategoría seleccionada si existe, si no, usa la categoría principal
        finalCategoryId = this.query.expensetypecatalog
        for (let i = 0; i < this.selectedSubcategories.length; i += 1) {
          if (this.selectedSubcategories[i]) {
            finalCategoryId = this.selectedSubcategories[i]
          } else {
            break
          }
        }
      }

      const column = this.getSortColumn()
      let order = null
      if (column) {
        order = this.sortDesc ? 'DESC' : 'ASC'
      }
      this.fetchExpenses({
        projectId: this.scopeProjectId,
        customerId: this.scopeCustomerId,
        status: this.query.status,
        supplier: this.query.supplier,
        name: this.query.name,
        dates,
        expensetypecatalog: finalCategoryId,
        column,
        order,
        pagination: this.expensesPagination,
      })
        .then(() => {
          this.$nextTick(() => {
            this.loading = false
          })
        })
        .catch(error => {
          console.error('❌ Error al cargar egresos:', error)
          this.loading = false
        })
    },

    handleSortChange({ sortBy, sortDesc }) {
      this.sortBy = sortBy
      this.sortDesc = sortDesc
      this.filterExpenses({ resetPage: true })
    },

    onCategoryChange(categoryId) {
      this.query.expensetypecatalog = categoryId
      this.subcategories = []
      this.selectedSubcategories = []

      if (categoryId) {
        this.fetchSubExpenseTypeCatalog({
          projectId: this.scopeProjectId,
          expenseTypeId: categoryId,
        }).then(category => {
          if (category.subcategories?.length) {
            // Solo agregamos si existen subcategorías
            this.subcategories = [category.subcategories]
            this.selectedSubcategories = [null] // Default: sin subcategoría
          }
        })
      }
    },

    onSubcategoryChange(subcategoryId, index) {
      if (subcategoryId === null) {
        // Si se selecciona "Sin Subcategoría", no hacemos fetch ni agregamos nuevas subcategorías
        // Pero mantenemos visible el combo
        this.selectedSubcategories[index] = null
        this.subcategories.splice(index + 1) // Elimina solo las siguientes, si existieran
        this.selectedSubcategories.splice(index + 1)
        return
      }

      this.selectedSubcategories[index] = subcategoryId
      this.subcategories.splice(index + 1)
      this.selectedSubcategories.splice(index + 1)

      this.fetchSubExpenseTypeCatalog({
        projectId: this.scopeProjectId,
        expenseTypeId: subcategoryId,
      }).then(category => {
        if (category.subcategories?.length) {
          this.subcategories.push(category.subcategories)
        }
      })
    },

    formatMoney(amount) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(amount)
    },
  },
}
</script>

<style scoped>
.expenses-mobile-view {
  font-size: 14px;
}

.float-button {
  position: fixed;
  bottom: 70px;
  right: 20px;
  z-index: 1000;
}

.add-button {
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-content {
  min-height: 120px;
}
</style>
